<script>
  import { format } from 'svelte-i18n';
  import { format as formatDate } from 'date-fns';

  import { Recipient } from '@client/components/recipient';

  import { ActivityItemEvent } from '../item-event';
  import { ActivityItemLocation } from '../item-location';
  import { ActivityItemMessage } from '../item-message';
  import { ActivityItemNote } from '../item-note';
  import { ActivityItemRecipient } from '../item-recipient';
  import { ActivityItemSender } from '../item-sender';

  /**
   * @typedef {Object} ActivityNote
   * @property {string} [ua]
   * @property {string} [isp]
   * @property {string} [country]
   * @property {string} [location]
   * @property {string} [filename]
   * @property {string} [hash]
   * @property {string} [url]
   * @property {string} [linkname]
   * @property {string} [page_title]
   * @property {string} [page_url]
   * @property {string} [objtype]
   * @property {string} [name]
   * @property {string|number} [slide]
   * @property {string} [adid]

   * @typedef {Object} ActivityEntry
   * @property {string} [verb]
   * @property {string} [objecttype]
   * @property {string} [ip]
   * @property {string} [recipient]
   * @property {string} [utcts] - UTC timestamp string
   * @property {ActivityNote} [note]
   */

  /**
   * The recipient associated with the activity history.
   * Can be a user email or "all".
   * @type {string}
   */
  export let recipient;

  /**
   * An array of activity history entries, grouped by date.
   * Format: { [date: string]: ActivityEntry[] }
   * @type {Object.<string, ActivityEntry[]>}
   */
  export let history = [];

  /**
   * Whether activity items should be visually grouped by recipient.
   * @type {boolean}
   */
  export let grouped = false;

  /**
   * Whether to show image previews for activity items
   * @type {boolean}
   */
  export let previews = true;

  /**
   * Whether to include the sender details in the activity items.
   * @type {boolean}
   */
  export let includeSender = false;

  /**
   * Whether to allow reporting of suspicious activity.
   * @type {boolean}
   */
  export let allowReporting = false;

  /**
   * Date format string for rendering human-readable dates.
   * @type {string}
   */
  const DATE_FORMAT = 'EEEE, MMM d, yyyy';

  /**
   * Time format string for rendering time of day.
   * @type {string}
   */
  const TIME_PERIOD = 'h:mm:ss aa';

  /**
   * Transformed list of history items as [date, entries] tuples.
   * @type {[string, ActivityEntry[]][]}
   */
  $: items = Object.entries(history);
</script>

<div class="recipient-history">
  {#if recipient !== 'all'}
    <Recipient {recipient} {history} />
  {/if}

  {#if !items.length}
    <div>
      <div class="empty">
        {$format('label.ACTIVITY_HISTORY_TIMELINE_EMPTY')}
      </div>
    </div>
  {:else}
    {#each items as [date, entries]}
      <div class="day-block">
        <div class="date">{formatDate(new Date(date), DATE_FORMAT)}</div>
        <div class="timeline" class:grouped class:includes-sender={includeSender}>
          {#each entries as entry, index}
            <div class="item">
              <ActivityItemEvent {entry} />
              <div class="time">{formatDate(new Date(entry.utcts), TIME_PERIOD)}</div>
              <div class="details">
                {#if entry.replied}
                  <div class="replied-badge">
                    {$format('label.REPLIED')}
                  </div>
                {/if}
                {#if entry.discarded}
                  <div class="discarded-badge">
                    {$format('label.DISCARDED')}
                  </div>
                {/if}
                <ActivityItemRecipient {entries} {entry} {recipient} {index} {date} />
                <ActivityItemMessage {entry} />
                <ActivityItemNote {entry} {previews} />
                <ActivityItemLocation {entry} {allowReporting} />
                <ActivityItemSender {entry} {includeSender} />
              </div>
            </div>
          {/each}
        </div>
      </div>
    {/each}
  {/if}
</div>

<style lang="scss" src="./item.scss"></style>
